var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import moment from 'moment';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PlanType } from '../../enum';
import { useSeatCapacityCheckAllAPI } from '../home/selectors';
import { useReportAPI } from '../report/selectors';
import { isSeatCapacityAlertsEnabled } from './featureFlag';
import { SeatsAlertDetailsDialog } from './seatsAlertDetailsDialog';
/** Background applied to cells/rows/chart bands whose seat demand exceeds available inventory. */
export var SEAT_EXCEEDED_COLOR = '#F3E2E2';
/** DOM anchor id of the Summary view's "Seats Required" section. */
export var SEATS_REQUIRED_SECTION_ID = 'seats-required-section';
/**
 * Map a period column index (0-based) to a calendar (year, month) the way the backend does when
 * bucketing seat demand: weekly plans assign a week to the calendar month its start date falls in,
 * monthly plans advance one calendar month per column. `month` is 1-12 to match the backend.
 */
export var periodIndexToYearMonth = function (startDate, planType, index) {
  var m = moment.utc(startDate).add(index, planType === PlanType.Weekly ? 'w' : 'M');
  return {
    year: m.year(),
    month: m.month() + 1
  };
};
var EMPTY = {
  resultsByPlanId: new Map(),
  refetch: function () {},
  openAlertDetails: function () {}
};
var SeatCapacityContext = createContext(EMPTY);
/**
 * Fetches seat-capacity results for every scenario of the current base plan once and shares them
 * with all report views. Mounted inside the report shell so a single request backs the Summary,
 * Weekly/Monthly, Daily and Interval exceed indicators. Alerts in the UI are Baseline-only.
 */
export var SeatCapacityProvider = function (_a) {
  var children = _a.children;
  var basePlanId = useParams().basePlanId;
  var reportData = useReportAPI()[0].data;
  var _b = useSeatCapacityCheckAllAPI(basePlanId ? Number(basePlanId) : undefined),
    seatData = _b[0],
    fetchSeatData = _b[1];
  var _c = useState(false),
    detailsOpen = _c[0],
    setDetailsOpen = _c[1];
  var baselinePlanId = useMemo(function () {
    var _a, _b;
    return (_b = (_a = reportData === null || reportData === void 0 ? void 0 : reportData.scenarios) === null || _a === void 0 ? void 0 : _a.find(function (s) {
      return s.isBaseline;
    })) === null || _b === void 0 ? void 0 : _b.id;
  }, [reportData === null || reportData === void 0 ? void 0 : reportData.scenarios]);
  useEffect(function () {
    if (basePlanId && isSeatCapacityAlertsEnabled()) fetchSeatData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basePlanId]);
  var resultsByPlanId = useMemo(function () {
    var map = new Map();
    var raw = seatData.data;
    if (!raw) return map;
    Object.entries(raw).forEach(function (_a) {
      var _b, _c;
      var planIdStr = _a[0],
        result = _a[1];
      var byLocation = new Map();
      ((_b = result.locations) !== null && _b !== void 0 ? _b : []).forEach(function (loc) {
        return byLocation.set(loc.locationName, loc);
      });
      map.set(Number(planIdStr), {
        anyExceeded: !!result.hasWarnings,
        exceededLocations: (_c = result.exceeded) !== null && _c !== void 0 ? _c : [],
        byLocation: byLocation
      });
    });
    return map;
  }, [seatData.data]);
  var openAlertDetails = useCallback(function () {
    return setDetailsOpen(true);
  }, []);
  var closeAlertDetails = useCallback(function () {
    return setDetailsOpen(false);
  }, []);
  var baselineExceededLocations = useMemo(function () {
    var _a, _b;
    if (baselinePlanId == null) return [];
    return (_b = (_a = resultsByPlanId.get(baselinePlanId)) === null || _a === void 0 ? void 0 : _a.exceededLocations) !== null && _b !== void 0 ? _b : [];
  }, [baselinePlanId, resultsByPlanId]);
  var value = useMemo(function () {
    return {
      resultsByPlanId: resultsByPlanId,
      baselinePlanId: baselinePlanId,
      refetch: fetchSeatData,
      openAlertDetails: openAlertDetails
    };
  }, [resultsByPlanId, baselinePlanId, fetchSeatData, openAlertDetails]);
  return _jsxs(SeatCapacityContext.Provider, __assign({
    value: value
  }, {
    children: [children, isSeatCapacityAlertsEnabled() && _jsx(SeatsAlertDetailsDialog, {
      open: detailsOpen,
      locations: baselineExceededLocations,
      onClose: closeAlertDetails
    })]
  }));
};
/**
 * True when the given plan id is the baseline of the current report family. Non-baseline
 * scenarios never surface seat-alert icons/highlights in the UI. Also gated on the feature flag —
 * false here is what turns off every icon/highlight/band/dialog check below while the alerts are
 * disabled, without touching each view's rendering.
 */
var isBaselinePlan = function (planId, baselinePlanId) {
  return isSeatCapacityAlertsEnabled() && planId != null && baselinePlanId != null && planId === baselinePlanId;
};
/**
 * Read seat-capacity exceed status by scenario plan id + location name (and optionally a calendar
 * month). Locations with no allocation are uncapped and never reported, so every lookup defaults
 * to "not exceeded" — safe for views that render locations the check doesn't cover.
 * All public checkers return false for non-baseline plan ids.
 */
export var useSeatCapacity = function () {
  var _a = useContext(SeatCapacityContext),
    resultsByPlanId = _a.resultsByPlanId,
    baselinePlanId = _a.baselinePlanId,
    refetch = _a.refetch,
    openAlertDetails = _a.openAlertDetails;
  var getExceededLocations = useCallback(function (planId) {
    var _a, _b;
    if (!isBaselinePlan(planId, baselinePlanId)) return [];
    return (_b = (_a = resultsByPlanId.get(planId)) === null || _a === void 0 ? void 0 : _a.exceededLocations) !== null && _b !== void 0 ? _b : [];
  }, [resultsByPlanId, baselinePlanId]);
  var isScenarioExceeded = useCallback(function (planId) {
    var _a;
    return isBaselinePlan(planId, baselinePlanId) && !!((_a = resultsByPlanId.get(planId)) === null || _a === void 0 ? void 0 : _a.anyExceeded);
  }, [resultsByPlanId, baselinePlanId]);
  var isLocationExceeded = useCallback(function (planId, locationName) {
    var _a;
    if (!isBaselinePlan(planId, baselinePlanId) || !locationName) return false;
    var loc = (_a = resultsByPlanId.get(planId)) === null || _a === void 0 ? void 0 : _a.byLocation.get(locationName);
    return !!loc && loc.byMonth.some(function (m) {
      return m.exceeded;
    });
  }, [resultsByPlanId, baselinePlanId]);
  var isLocationMonthExceeded = useCallback(function (planId, locationName, year, month) {
    var _a;
    if (!isBaselinePlan(planId, baselinePlanId) || !locationName) return false;
    var loc = (_a = resultsByPlanId.get(planId)) === null || _a === void 0 ? void 0 : _a.byLocation.get(locationName);
    return !!loc && loc.byMonth.some(function (m) {
      return m.year === year && m.month === month && m.exceeded;
    });
  }, [resultsByPlanId, baselinePlanId]);
  /**
   * Available inventory (capacity − committed) for a location in a given month, or undefined when
   * the location is uncapped (no allocation) or the plan is not the baseline. Views compare their
   * own rendered per-period seats against this to flag the exact week/day/interval that exceeds —
   * finer than the backend's month-level `exceeded` flag, which peaks demand across the whole month.
   */
  var getAvailable = useCallback(function (planId, locationName, year, month) {
    var _a, _b;
    if (!isBaselinePlan(planId, baselinePlanId) || !locationName) return undefined;
    var loc = (_a = resultsByPlanId.get(planId)) === null || _a === void 0 ? void 0 : _a.byLocation.get(locationName);
    return (_b = loc === null || loc === void 0 ? void 0 : loc.byMonth.find(function (m) {
      return m.year === year && m.month === month;
    })) === null || _b === void 0 ? void 0 : _b.available;
  }, [resultsByPlanId, baselinePlanId]);
  /** True when ANY location of the baseline exceeds in the given month — drives period header icons. */
  var isScenarioMonthExceeded = useCallback(function (planId, year, month) {
    if (!isBaselinePlan(planId, baselinePlanId)) return false;
    var scenario = resultsByPlanId.get(planId);
    if (!scenario) return false;
    return Array.from(scenario.byLocation.values()).some(function (loc) {
      return loc.byMonth.some(function (m) {
        return m.year === year && m.month === month && m.exceeded;
      });
    });
  }, [resultsByPlanId, baselinePlanId]);
  return {
    baselinePlanId: baselinePlanId,
    isScenarioExceeded: isScenarioExceeded,
    getExceededLocations: getExceededLocations,
    isLocationExceeded: isLocationExceeded,
    isLocationMonthExceeded: isLocationMonthExceeded,
    isScenarioMonthExceeded: isScenarioMonthExceeded,
    getAvailable: getAvailable,
    refetch: refetch,
    openAlertDetails: openAlertDetails
  };
};
/**
 * Resolve a scenario's plan id from its `colorIndex`. The period/daily payloads don't carry the
 * plan id, but every scenario in `reportData.scenarios` (the authoritative plan list) does, and
 * colorIndex is unique per scenario. Returns a stable lookup function.
 */
export var useScenarioPlanIdByColorIndex = function () {
  var reportData = useReportAPI()[0].data;
  return useCallback(function (colorIndex) {
    var _a, _b;
    return colorIndex == null ? undefined : (_b = (_a = reportData === null || reportData === void 0 ? void 0 : reportData.scenarios) === null || _a === void 0 ? void 0 : _a.find(function (s) {
      return s.colorIndex === colorIndex;
    })) === null || _b === void 0 ? void 0 : _b.id;
  }, [reportData === null || reportData === void 0 ? void 0 : reportData.scenarios]);
};